import { z } from 'zod';
import { DecimalString, IsoDateTime, UuidString } from '../primitives.js';
import { ProjectTaskStatus } from './project-task.js';

/**
 * S18 — Canonical Gantt-shape response для GET /api/v1/projects/:id/gantt (P7.8).
 * Independent от Gantt-библиотеки — frontend G8 адаптирует в `gantt-task-react` shape.
 *
 * baseline_start/end зарезервированы (всегда null в этом релизе; заполнятся фичей
 * baseline-snapshot в S22).
 */

export const GanttNodeType = z.enum(['project', 'task', 'milestone', 'group']);
export type GanttNodeType = z.infer<typeof GanttNodeType>;

export const GanttDependencyType = z.enum(['finish_to_start']);
export type GanttDependencyType = z.infer<typeof GanttDependencyType>;

export const GanttTask = z
  .object({
    id: UuidString,
    name: z.string(),
    start: IsoDateTime,
    end: IsoDateTime,
    progress: DecimalString,
    parent_id: UuidString.nullable().optional(),
    type: GanttNodeType,
    assignee_user_id: UuidString.nullable().optional(),
    assignee_name: z.string().nullable().optional(),
    status: ProjectTaskStatus.optional(),
    weight: DecimalString.optional(),
    has_children: z.boolean().default(false),
    /** S22 baseline fields — null until baseline-snapshot feature. */
    baseline_start: IsoDateTime.nullable().optional(),
    baseline_end: IsoDateTime.nullable().optional(),
    /** BE-PROJ-7 — true when task is on critical path (zero-slack CPM). */
    is_critical: z.boolean().default(false).optional(),
  })
  .passthrough();
export type GanttTask = z.infer<typeof GanttTask>;

export const GanttMilestone = z
  .object({
    id: UuidString,
    name: z.string(),
    start: IsoDateTime,
    end: IsoDateTime,
    is_reached: z.boolean(),
    billing_pct: DecimalString.optional(),
    /** B5 — optional привязка к task для UX15 visual alignment. */
    linked_task_id: UuidString.nullable().optional(),
  })
  .passthrough();
export type GanttMilestone = z.infer<typeof GanttMilestone>;

export const GanttDependency = z
  .object({
    from_id: UuidString,
    to_id: UuidString,
    type: GanttDependencyType,
  })
  .passthrough();
export type GanttDependency = z.infer<typeof GanttDependency>;

export const GanttData = z
  .object({
    project: z
      .object({
        id: UuidString,
        name: z.string(),
        start: IsoDateTime.nullable(),
        end: IsoDateTime.nullable(),
        progress: DecimalString,
        type: z.literal('project'),
      })
      .passthrough(),
    tasks: z.array(GanttTask),
    milestones: z.array(GanttMilestone),
    dependencies: z.array(GanttDependency),
    today: IsoDateTime,
  })
  .passthrough();
export type GanttData = z.infer<typeof GanttData>;
